import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ASSETS, SERVICES, COMPANY_INFO } from '../config/assets';
import { CheckCircle, Sun, Droplets, Shield, TreePine, ArrowRight, Waves, Play, Mail, Phone, MapPin, Send, Mountain, ChevronLeft, ChevronRight, Share } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const HonedConcretePage = () => {
  const service = SERVICES.find(s => s.slug === 'honed-concrete');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    serviceType: 'Honed Concrete'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // All Honed Concrete project images from the previous website + new uploads
  const allHonedConcreteImages = [
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/hrd4psv4_IMG-20250510-WA0047.jpg', title: 'Premium Pool Surround Project', description: 'High-end pool area with matte finish honed concrete and penetrating sealer' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/rtbqvfeh_IMG-20250423-WA0026.jpg', title: 'Luxury Pool Surround Honed Concrete', description: 'Full aggregate exposure honed concrete pool area with slip-resistant matte finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/dte0r2uu_IMG-20250423-WA0025.jpg', title: 'Modern Pool Area Honed Finish', description: 'Outdoor pool surround with penetrating sealer and natural aggregate texture' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/bg79nw4k_IMG-20250423-WA0019.jpg', title: 'Pool Deck Honed Concrete Project', description: 'Slip-resistant pool deck with weather-resistant honed concrete finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/nedtajwy_IMG-20250423-WA0020.jpg', title: 'Residential Pool Honed Concrete', description: 'Luxury residential pool with full aggregate exposure honed finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ohoagpvk_IMG-20250423-WA0028.jpg', title: 'Outdoor Entertainment Area Honed Concrete', description: 'Full aggregate exposure honed concrete with slip-resistant matte finish for outdoor dining' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/2z5fxu85_IMG-20250423-WA0030.jpg', title: 'Backyard Dining Area Honed Concrete', description: 'Outdoor entertaining space with penetrating sealer and natural aggregate texture' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/aof9vuku_IMG-20250423-WA0031.jpg', title: 'Patio Honed Concrete Project', description: 'Slip-resistant outdoor patio with full aggregate exposure and weather-resistant finish' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/edktgix2_IMG-20250510-WA0045.jpg', title: 'Outdoor Honed Concrete Stairs', description: 'Slip-resistant honed concrete stairs with matte finish for safety' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/0gm1sxm0_IMG-20250510-WA0044.jpg', title: 'Honed Concrete Steps Project', description: 'Outdoor steps with penetrating sealer and anti-slip surface' },
    { src: 'https://customer-assets.emergentagent.com/job_concretepro/artifacts/w8465erc_IMG-20250510-WA0043.jpg', title: 'Staircase Honed Concrete Finish', description: 'Commercial outdoor staircase with weather-resistant honed finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20250228_150521-scaled.jpg', title: 'Outdoor Honed Concrete', description: 'Matte finish outdoor area with slip-resistant surface' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20250128_155520-scaled.jpg', title: 'Pool Surround Project', description: 'Slip-resistant honed concrete around pool area' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20250128_151403-scaled.jpg', title: 'Patio Honing Work', description: 'Outdoor entertaining area honed concrete' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20250128_150942-scaled.jpg', title: 'Penetrating Sealer Application', description: 'Professional sealer application for protection' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20250126_111528-scaled.jpg', title: 'Driveway Honed Finish', description: 'Flat gradient driveway with matte finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20250126_111335-scaled.jpg', title: 'Outdoor Sitting Area', description: 'Relaxation space with slip-resistant honed concrete' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20250125_131746-scaled.jpg', title: 'Footpath Honing Project', description: 'Pedestrian walkway with safety-rated surface' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20240914_152126-scaled.jpg', title: 'Weather Resistant Finish', description: 'UV stable honed concrete for outdoor exposure' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20240415_161530-scaled.jpg', title: 'Outdoor Entertainment Area', description: 'Matte finish perfect for outdoor dining spaces' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20240415_154951-scaled.jpg', title: 'Aggregate Exposure Detail', description: 'Full aggregate exposure in honed finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20240415_153848-scaled.jpg', title: 'Safety Compliance Project', description: 'Slip-resistance testing and compliance verification' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220129_145613-scaled.jpg', title: 'Natural Texture Finish', description: 'Completely matte surface maintaining natural character' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220129_145000-scaled.jpg', title: 'Balcony Honed Concrete', description: 'Outdoor balcony flooring with penetrating sealer' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220129_135110-scaled.jpg', title: 'Process Documentation', description: 'Honing process showing equipment and technique' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220129_134614-scaled.jpg', title: 'Equipment in Action', description: 'Professional honing equipment creating matte finish' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220129_134607-scaled.jpg', title: 'Surface Preparation', description: 'Initial grinding before honing process' },
    { src: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210616_145842-scaled.jpg', title: 'Completed Honed Project', description: 'Finished outdoor honed concrete installation' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allHonedConcreteImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allHonedConcreteImages.length) % allHonedConcreteImages.length);
  };

  const goToSlide = (index) => {
    setCurrentImageIndex(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Thank you! Your message has been sent successfully.');
        setFormData({ name: '', email: '', phone: '', message: '', serviceType: 'Honed Concrete' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const keyFeatures = [
    { icon: Shield, title: 'Slip-Resistant Safety', description: 'Certified slip resistance ratings for wet conditions and outdoor use' },
    { icon: Sun, title: 'UV Stable Finish', description: 'Weather-resistant surface that won\'t fade under harsh sunlight' },
    { icon: Droplets, title: 'Penetrating Protection', description: 'Deep penetrating sealers maintain breathability while protecting' },
    { icon: Mountain, title: 'Completely Matte', description: 'Zero gloss natural finish that complements outdoor environments' }
  ];

  const idealApplications = [
    'Pool surrounds and deck areas',
    'Footpaths and pedestrian walkways',
    'Porches and outdoor entertaining spaces',
    'Flat or low gradient driveways',
    'Patio and courtyard areas',
    'Outdoor dining and relaxation zones',
    'Garden pathways and landscaping',
    'Commercial outdoor spaces'
  ];

  const technicalAdvantages = [
    { title: 'Slip Resistance', description: 'Meets Australian standards for slip resistance in wet conditions' },
    { title: 'Breathable Surface', description: 'Penetrating sealers allow moisture vapor transmission' },
    { title: 'UV Protection', description: 'Color stability and surface integrity under extreme weather' },
    { title: 'Easy Maintenance', description: 'Simple cleaning with pressure washing and minimal upkeep' },
    { title: 'Natural Aesthetics', description: 'Complements landscaping and outdoor architectural features' },
    { title: 'Thermal Comfort', description: 'Stays cooler underfoot compared to other outdoor surfaces' }
  ];

  const processSteps = [
    { step: '01', title: 'Surface Assessment', description: 'Evaluate concrete condition and prepare for outdoor application requirements' },
    { step: '02', title: 'Diamond Honing', description: 'Mechanical honing to achieve completely matte finish with optimal texture' },
    { step: '03', title: 'Slip Resistance Testing', description: 'Verify surface meets required slip resistance ratings for safety compliance' },
    { step: '04', title: 'Penetrating Sealer', description: 'Apply deep penetrating sealer for protection while maintaining breathability' },
    { step: '05', title: 'Final Inspection', description: 'Quality control check for consistency, safety, and weather readiness' }
  ];

  const weatherBenefits = [
    { weather: 'Rain & Wet Conditions', benefit: 'Slip-resistant surface provides safety in wet weather' },
    { weather: 'Intense Sun & Heat', benefit: 'UV stable finish won\'t fade, stays cooler than alternatives' },
    { weather: 'Freeze-Thaw Cycles', benefit: 'Penetrating sealers allow natural expansion without damage' },
    { weather: 'High Humidity', benefit: 'Breathable surface prevents moisture buildup underneath' }
  ];

  // Sample YouTube video data - you can update these with actual video IDs
  const youtubeVideos = [
    {
      id: 'bTWFrSThq0Q', // Actual video from your channel
      title: 'Honed Concrete Pool Surround Project',
      description: 'See our slip-resistant honed concrete installation around a luxury pool'
    },
    {
      id: 'XHSL2KrPJ8Y', // Actual video from your channel
      title: 'Outdoor Patio Honed Concrete Transformation',
      description: 'Transform outdoor spaces with beautiful matte honed concrete'
    },
    {
      id: 'w0QCP38_P-4', // Actual video from your channel
      title: 'Honed Concrete Maintenance Guide',
      description: 'How to maintain your honed concrete outdoor surfaces'
    }
  ];

  // Sample YouTube Shorts data - you can update these with actual video IDs
  const youtubeShorts = [
    {
      id: '7g0y2GF5Q2Y', // Actual short from your channel
      title: 'Outdoor Honed Process',
      description: 'Quick outdoor honing technique'
    },
    {
      id: 'aK9In-pMLFA', // Actual short from your channel
      title: 'Slip Resistance Test',
      description: 'Safety testing for outdoor areas'
    },
    {
      id: 'pLbaROEO-vg', // Actual short from your channel
      title: 'Matte Finish Demo',
      description: 'See the perfect matte finish'
    },
    {
      id: 'nNo-JHtv4J8', // Actual short from your channel
      title: 'Pool Area Honing',
      description: 'Slip-resistant pool surrounds'
    },
    {
      id: '6NmMpvUzq68', // Actual short from your channel
      title: 'Penetrating Sealer',
      description: 'Outdoor protection application'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700">
      {/* Hero Video Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="honed-concrete-hero-video"
        >
          <source src={service.video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="max-w-4xl"
          >
            <Badge variant="secondary" className="mb-4 bg-white/20 text-white border-white/30">
              Outdoor Specialist
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              Honed
              <span className="bg-gradient-to-r from-emerald-300 to-teal-400 bg-clip-text text-transparent block">
                Concrete
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              The perfect matte finish for outdoor living. Slip-resistant, weather-resistant, and naturally beautiful.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0 shadow-2xl"
              asChild
            >
              <Link to="/contact" data-testid="hero-cta-button">
                Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              The Ultimate Outdoor Concrete Finish
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Honed concrete delivers a completely matte finish that's specifically designed for outdoor applications. 
              Perfect for footpaths, porches, pool surrounds, flat or low gradient driveways, and any outdoor sitting 
              and relaxing area, this finish combines safety with natural beauty.
            </p>
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Using penetrating sealers rather than surface coatings, honed concrete maintains the natural breathability 
              of concrete while providing excellent protection. The slip-resistant surface meets safety ratings for wet 
              conditions, making it ideal for pool areas and outdoor entertaining spaces.
            </p>
          </div>
        </motion.section>

        {/* Key Features */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Why Choose Honed Concrete?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950">
                  <CardContent className="p-6 text-center">
                    <feature.icon className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                    <h4 className="font-semibold mb-2 text-slate-800 dark:text-slate-100">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Applications */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6 text-slate-800 dark:text-slate-100">
                Perfect Outdoor Applications
              </h3>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                Honed concrete is the go-to choice for outdoor areas where safety, durability, and natural beauty 
                are paramount. The matte finish complements outdoor environments while providing the slip resistance 
                and weather protection needed for Australian conditions.
              </p>
              <div className="grid gap-3">
                {idealApplications.map((app, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-200">{app}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <Card className="p-6 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 border-slate-300 dark:border-slate-600">
                <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                  Weather Performance
                </h4>
                <div className="space-y-4">
                  {weatherBenefits.map((item, index) => (
                    <div key={index} className="p-3 bg-emerald-50 dark:bg-emerald-950 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      <h5 className="font-medium text-emerald-800 dark:text-emerald-200 mb-1">{item.weather}</h5>
                      <p className="text-sm text-emerald-700 dark:text-emerald-300">{item.benefit}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </motion.section>

        {/* Technical Advantages */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Technical Advantages
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technicalAdvantages.map((advantage, index) => (
              <motion.div
                key={advantage.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                        {advantage.title}
                      </h4>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {advantage.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Process */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Our Honing Process
          </h3>
          <div className="max-w-4xl mx-auto">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start mb-8 last:mb-0"
              >
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-6 flex-shrink-0">
                  {step.step}
                </div>
                <div>
                  <h4 className="text-xl font-semibold mb-2 text-slate-800 dark:text-slate-100">
                    {step.title}
                  </h4>
                  <p className="text-slate-600 dark:text-slate-300">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Honed Concrete Project Gallery Carousel */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Honed Concrete Project Gallery
          </h3>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              <div className="relative">
                {/* Main Image Display */}
                <div className="aspect-video relative overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <img
                    src={allHonedConcreteImages[currentImageIndex].src}
                    alt={allHonedConcreteImages[currentImageIndex].title}
                    className="w-full h-full object-cover transition-opacity duration-500"
                    data-testid="honed-concrete-carousel-main-image"
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="honed-concrete-carousel-prev-button"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                    data-testid="honed-concrete-carousel-next-button"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {allHonedConcreteImages.length}
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950">
                  <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    {allHonedConcreteImages[currentImageIndex].title}
                  </h4>
                  <p className="text-slate-600 dark:text-slate-300">
                    {allHonedConcreteImages[currentImageIndex].description}
                  </p>
                </div>

                {/* Thumbnail Strip */}
                <div className="p-4 bg-slate-100 dark:bg-slate-800">
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {allHonedConcreteImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          index === currentImageIndex 
                            ? 'border-emerald-500 dark:border-emerald-400' 
                            : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                        data-testid={`honed-concrete-thumbnail-${index}`}
                      >
                        <img
                          src={image.src}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Carousel Controls */}
            <div className="text-center mt-6">
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                {allHonedConcreteImages.length} outdoor honed concrete projects showcasing slip-resistant safety and natural beauty
              </p>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="outline"
                  onClick={prevImage}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={nextImage}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950"
                >
                  Next <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* YouTube Videos */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Watch Honed Concrete in Action
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {youtubeVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-video">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={video.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      data-testid={`youtube-video-${index}`}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {video.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {video.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-videos-button"
            >
              View All Videos on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* YouTube Shorts */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
            Quick Honed Concrete Tips - YouTube Shorts
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {youtubeShorts.map((short, index) => (
              <motion.div
                key={short.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative aspect-[9/16]">
                    <iframe
                      src={`https://www.youtube.com/embed/${short.id}?hd=1&vq=hd1080&rel=0&modestbranding=1`}
                      title={short.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                    {/* Shorts Label */}
                    <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 text-xs rounded font-medium pointer-events-none z-10">
                      Shorts
                    </div>
                    {/* Share indicator */}
                    <div className="absolute top-3 right-3 bg-black/70 text-white p-1.5 rounded pointer-events-none z-10">
                      <Share className="w-3 h-3" />
                    </div>
                    {/* Share indicator */}
                    <div className="absolute top-3 right-3 bg-black/70 text-white p-1.5 rounded pointer-events-none z-10">
                      <Share className="w-3 h-3" />
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 text-slate-800 dark:text-slate-100">
                      {short.title}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {short.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button
              variant="outline"
              size="lg"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950"
              onClick={() => window.open(COMPANY_INFO.youtube, '_blank')}
              data-testid="view-all-shorts-button"
            >
              View All Shorts on YouTube <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.section>

        {/* Contact Form Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="max-w-6xl mx-auto">
            <h3 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">
              Request Your Honed Concrete Quote
            </h3>
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <Card className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="honed-concrete-contact-form">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 dark:text-slate-200">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-slate-700 dark:text-slate-200">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="mt-1"
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 dark:text-slate-200">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+614 XX XXX XXX"
                      className="mt-1"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-slate-700 dark:text-slate-200">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your outdoor honed concrete project - area type (pool, driveway, patio), size, slip resistance requirements..."
                      rows={5}
                      className="mt-1"
                      data-testid="contact-message-textarea"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>
                        Send Honed Concrete Inquiry <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </Card>
              
              {/* Contact Information */}
              <div className="space-y-8">
                <Card className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950 border-emerald-200 dark:border-emerald-800">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    Outdoor Concrete Specialists
                  </h4>
                  <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                    We specialize in creating beautiful, safe outdoor concrete surfaces that complement 
                    your lifestyle while meeting all safety and durability requirements.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-emerald-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-emerald-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      <span className="text-slate-700 dark:text-slate-200">{COMPANY_INFO.address}</span>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6">
                  <h4 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-100">
                    What to Expect
                  </h4>
                  <div className="space-y-3 text-slate-600 dark:text-slate-300">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Site assessment for outdoor application suitability</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Slip resistance testing and safety compliance</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Weather-appropriate sealer selection and application</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>Long-term maintenance guidance</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Card className="p-12 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-100 dark:to-slate-200 text-white dark:text-slate-900">
            <TreePine className="w-16 h-16 text-emerald-400 dark:text-emerald-600 mx-auto mb-6" />
            <h3 className="text-3xl font-bold mb-4">
              Perfect for Outdoor Living
            </h3>
            <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
              Create beautiful, safe outdoor spaces with honed concrete. The ideal choice for pools, 
              patios, driveways, and any area where you want to enjoy the outdoors safely.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0"
                onClick={() => document.querySelector('[data-testid="honed-concrete-contact-form"]').scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-cta-contact-button"
              >
                Get Your Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white hover:text-slate-900 dark:border-slate-900 dark:text-slate-900 dark:hover:bg-slate-900 dark:hover:text-white"
                asChild
              >
                <Link to="/portfolio" data-testid="final-cta-portfolio-button">
                  View Outdoor Projects
                </Link>
              </Button>
            </div>
          </Card>
        </motion.section>
      </div>
    </div>
  );
};

export default HonedConcretePage;